import React, { createContext, useCallback, useContext, useState } from 'react';
import type { FeatureFlags } from '../config/featureFlags';
import { defaultFeatureFlags } from '../config/featureFlags';

interface FeatureFlagsContextValue {
  flags: FeatureFlags;
  setFlag: (key: keyof FeatureFlags, value: boolean) => void;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: defaultFeatureFlags,
  setFlag: () => {},
});

const STORAGE_KEY = 'ff_overrides';

function loadOverrides(): Partial<FeatureFlags> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<FeatureFlags>) : {};
  } catch {
    return {};
  }
}

export function FeatureFlagsProvider({ children }: { children: React.ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlags>(() => ({
    ...defaultFeatureFlags,
    ...loadOverrides(),
  }));

  const setFlag = useCallback((key: keyof FeatureFlags, value: boolean) => {
    setFlags(prev => {
      const next = { ...prev, [key]: value };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...loadOverrides(), [key]: value }));
      } catch {
        // localStorage may be blocked in private browsing — silently ignore
      }
      return next;
    });
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ flags, setFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export const useFeatureFlags = () => useContext(FeatureFlagsContext).flags;
export const useSetFlag = () => useContext(FeatureFlagsContext).setFlag;
